import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { NotFoundInPage } from '@/components/NotFoundInPage';
import { ListEmpty, useListUrlState } from '@/components/lists';
import { getLogEntriesFlat } from '@/features/elva-work/store';
import type { MatchedDeposit, MatchedEra } from '../types';
import {
  businessDaysBetween,
  chipLabel,
  DATE_PRESET_LABELS,
  inRange,
  longDate,
  monoDay,
  POSTING_TODAY_ISO,
  presetOf,
  presetRange,
  shortDate,
  spanLabel,
  windowWords,
  type DatePreset,
  type IsoRange,
} from '../dates';
import { checkPostedBy, checkPostedLabel } from '../matchedMock';
import {
  getAccountsSync,
  getMatchedDepositSync,
  getMatchedDepositsSync,
  unmatchDeposit,
  undoUnmatch,
} from '../store';
import { money } from '../utils';
import { CoverageLine } from './Coverage';
import type { AddToast } from './StatementUpload';
import { isPlainClick } from '@/components/plainClick';
import { PMS } from '@/features/shared/pms';

const STEP = 50;

// ---------------------------------------------------------------------------
// words
// ---------------------------------------------------------------------------

const HOW_LABEL: Record<MatchedDeposit['kind'], string> = {
  trace: 'Trace',
  amount: 'Amount and date',
  checks: 'Checks',
  eras: 'Several ERAs',
};

function plural(n: number, one: string, many: string): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}

/** Line 2 of a row: the key reference. */
function keyRef(d: MatchedDeposit): string {
  if (d.kind === 'checks') {
    return `${plural(d.checks.length, 'check', 'checks')} · ${d.checks.map((c) => c.payer.replace(' of Texas', '')).join(', ')}`;
  }
  if (d.bankTrace) return `trace ${d.bankTrace}`;
  return 'no trace on bank line';
}

function claimTotal(d: MatchedDeposit): number {
  if (d.kind === 'checks') return d.checks.reduce((s, c) => s + c.claimCount, 0);
  return d.eras.reduce((s, e) => s + e.claims.length, 0);
}

function actor(name: string): string {
  return name.startsWith('Ava Thompson') ? 'Ava Thompson (admin)' : name;
}

function eraWords(d: MatchedDeposit): string {
  if (d.kind === 'checks') return d.bankLine.startsWith('MOBILE') ? 'mobile deposit' : 'remote deposit';
  const dates = d.eras.map((e) => shortDate(e.dateIso));
  if (dates.length === 1) return `ERA of ${dates[0] ?? ''}`;
  return `ERAs of ${dates.slice(0, -1).join(', ')} and ${dates[dates.length - 1] ?? ''}`;
}

function headline(d: MatchedDeposit): string {
  if (d.kind === 'checks') {
    const n = d.checks.length;
    return n === 1
      ? `1 insurance check was deposited, ${money(d.amount)}`
      : `${String(n)} insurance checks were deposited together, ${money(d.amount)}`;
  }
  if (d.kind === 'eras') {
    return `${d.payer} paid ${money(d.amount)} for ${String(d.eras.length)} ERAs and it reached the bank in one deposit`;
  }
  return `${d.payer} paid ${money(d.amount)} and it reached the bank`;
}

function pill(d: MatchedDeposit): string {
  switch (d.kind) {
    case 'trace': return '✓ Matched by trace number';
    case 'amount': return '✓ Matched by amount and date';
    case 'checks': return `✓ Matched as ${plural(d.checks.length, 'check', 'checks')}`;
    case 'eras': return `✓ Matched to ${String(d.eras.length)} ERAs`;
  }
}

// ---------------------------------------------------------------------------
// search and dates (the URL is the state: ?q=&from=&to=&account=&page=)
// ---------------------------------------------------------------------------

/** Exact amounts ("1204.65", "$1,204.65"), traces, check numbers, payers, bank-line text, claims and patients. */
function matchesQuery(d: MatchedDeposit, raw: string): boolean {
  const q = raw.trim().toLowerCase();
  if (q === '') return true;
  const looksLikeMoney = /^\$?\s*[\d,]+(\.\d{1,2})?$/.test(q);
  const hasMoneyMark = /[$.,]/.test(q);
  if (looksLikeMoney && hasMoneyMark) {
    return Math.abs(Number(q.replace(/[$,\s]/g, '')) - d.amount) < 0.005;
  }
  if (looksLikeMoney && Math.abs(Number(q.replace(/[$,\s]/g, '')) - d.amount) < 0.005) return true;
  return d.searchText.includes(q);
}

interface UrlState { q: string; from: string; to: string; account: string; page: string }

function rangeOf(s: UrlState): IsoRange {
  if (s.from === 'all') return { from: null, to: null };
  if (s.from === '' && s.to === '') return presetRange('this-month');
  return { from: s.from === '' ? null : s.from, to: s.to === '' ? POSTING_TODAY_ISO : s.to };
}

function urlForPreset(p: DatePreset): { from: string; to: string } {
  if (p === 'this-month') return { from: '', to: '' };
  if (p === 'all-time') return { from: 'all', to: '' };
  const r = presetRange(p);
  return { from: r.from ?? '', to: r.to ?? '' };
}

function DateChip({ range, onPick }: { range: IsoRange; onPick: (p: { from: string; to: string }) => void }) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const [from, setFrom] = useState(range.from ?? '');
  const [to, setTo] = useState(range.to ?? POSTING_TODAY_ISO);
  const ref = useRef<HTMLDivElement>(null);
  const preset = presetOf(range);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent): void => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const canApply = from !== '' && to !== '' && from <= to && to <= POSTING_TODAY_ISO;

  return (
    <div className="bm-dr" ref={ref}>
      <button
        type="button"
        className="bm-chip"
        aria-haspopup="menu"
        aria-expanded={open}
        title={DATE_PRESET_LABELS.find((p) => p.value === preset)?.label ?? 'Custom range'}
        onClick={() => { setOpen((v) => !v); }}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3.5" y="5" width="17" height="15" rx="2.5" /><path d="M3.5 10h17M8 3v4M16 3v4" /></svg>
        <span>{chipLabel(range)}</span>
        <span className="bm-cv" aria-hidden="true">▾</span>
      </button>
      {open ? (
        <div className="bm-menu" role="menu">
          {DATE_PRESET_LABELS.map((p) => (
            <button
              key={p.value}
              type="button"
              role="menuitemradio"
              aria-checked={preset === p.value}
              className={`bm-mi${preset === p.value ? ' on' : ''}`}
              onClick={() => {
                if (p.value === 'custom') { setCustom(true); return; }
                setCustom(false);
                setOpen(false);
                onPick(urlForPreset(p.value));
              }}
            >
              {p.label}
            </button>
          ))}
          {custom || preset === 'custom' ? (
            <div className="bm-custom">
              <label>From<input type="date" value={from} max={POSTING_TODAY_ISO} onChange={(e) => { setFrom(e.target.value); }} /></label>
              <label>To<input type="date" value={to} max={POSTING_TODAY_ISO} onChange={(e) => { setTo(e.target.value); }} /></label>
              <button type="button" className="po-btn po-btn-primary po-btn-sm" disabled={!canApply} aria-describedby={canApply ? undefined : 'bm-range-reason'} onClick={() => { setOpen(false); onPick({ from, to }); }}>Apply</button>
              {canApply ? null : <span id="bm-range-reason" className="po-disabled-reason">Pick a start date on or before the end date.</span>}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// the list + detail
// ---------------------------------------------------------------------------

export function MatchedView({ id, isDesktop, addToast }: { id?: string; isDesktop: boolean; addToast: AddToast }) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [s, patch] = useListUrlState({ q: '', from: '', to: '', account: '', page: '1' });
  const all = getMatchedDepositsSync();
  const accounts = getAccountsSync();
  const range = rangeOf(s);
  const q = s.q.trim();
  const page = Math.max(1, Number.parseInt(s.page, 10) || 1);

  const { sorted, outside } = useMemo(() => {
    const pool = s.account ? all.filter((d) => d.accountId === s.account) : all;
    const hits = q === '' ? pool : pool.filter((d) => matchesQuery(d, q));
    const win = hits.filter((d) => inRange(d.dateIso, range));
    const order = [...win].sort((a, b) => (a.dateIso === b.dateIso ? (a.id < b.id ? -1 : 1) : a.dateIso < b.dateIso ? 1 : -1));
    return { sorted: order, outside: q === '' ? 0 : hits.length - win.length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, s.account, q, range.from, range.to]);

  const shown = sorted.slice(0, STEP * page);

  // Bare /posting/matched opens the newest matched deposit on desktop.
  const first = sorted[0];
  useEffect(() => {
    if (id || !isDesktop || !first) return;
    void navigate(`/posting/matched/${first.id}${search}`, { replace: true });
  }, [id, isDesktop, first, navigate, search]);

  // ↑ / ↓ move through the rows.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (shown.length === 0) return;
      const i = shown.findIndex((d) => d.id === id);
      const next = e.key === 'ArrowDown' ? Math.min(shown.length - 1, i + 1) : Math.max(0, i === -1 ? 0 : i - 1);
      const target = shown[next];
      if (!target || target.id === id) return;
      e.preventDefault();
      void navigate(`/posting/matched/${target.id}${search}`);
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [shown, id, navigate, search]);

  // Keep the selected row in view when the arrows move it.
  const listRef = useRef<HTMLElement>(null);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>('[aria-current="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [id]);

  const groups: { iso: string; rows: MatchedDeposit[] }[] = [];
  for (const d of shown) {
    const last = groups[groups.length - 1];
    if (last?.iso === d.dateIso) last.rows.push(d);
    else groups.push({ iso: d.dateIso, rows: [d] });
  }

  const words = windowWords(range);
  const allTime = range.from === null;
  const searchAllTime = (): void => { patch({ from: 'all', to: '', page: '1' }); };

  const open = (did: string): void => { void navigate(`/posting/matched/${did}${search}`); };

  const selected = id ? getMatchedDepositSync(id) : null;
  const mobileList = !isDesktop && !id;
  const mobileDetail = !isDesktop && Boolean(id);

  const onUnmatch = (d: MatchedDeposit): void => {
    const rest = sorted.filter((x) => x.id !== d.id);
    const idx = sorted.findIndex((x) => x.id === d.id);
    const next = rest[Math.min(Math.max(idx, 0), rest.length - 1)];
    if (!unmatchDeposit(d.id)) return;
    addToast(
      `Unmatched the ${money(d.amount)} deposit of ${shortDate(d.dateIso)}. It is in Bank deposits.`,
      { label: 'Undo', onClick: () => { undoUnmatch(d.id); void navigate(`/posting/matched/${d.id}${search}`); } },
      9000,
    );
    void navigate(next && isDesktop ? `/posting/matched/${next.id}${search}` : `/posting/matched${search}`);
  };

  return (
    <div className="po-body">
      <aside ref={listRef} className={`po-queue bm-list${mobileDetail ? ' bm-hidden' : ''}`} aria-label="Matched deposits">
        <div className="bm-f">
          <label className="bm-srch">
            <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="7" cy="7" r="5" /><path d="M11 11l3.5 3.5" /></svg>
            <input
              className="bm-inp"
              type="search"
              placeholder="Search amount, trace, payer or text"
              aria-label="Search matched deposits"
              value={s.q}
              onChange={(e) => { patch({ q: e.target.value, page: '1' }); }}
            />
          </label>
          <div className="bm-frow">
            <DateChip key={`${range.from ?? ''}|${range.to ?? ''}`} range={range} onPick={(p) => { patch({ from: p.from, to: p.to, page: '1' }); }} />
            {accounts.length > 1 ? (
              <select
                className="bm-sel"
                aria-label="Bank account"
                value={s.account}
                onChange={(e) => { patch({ account: e.target.value, page: '1' }); }}
              >
                <option value="">All accounts</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.short}</option>)}
              </select>
            ) : null}
          </div>
          <CoverageLine />
        </div>

        {outside > 0 ? (
          <div className="bm-outside">
            {outside.toLocaleString()} more in other dates ·{' '}
            <button type="button" className="bm-link-btn" onClick={searchAllTime}>Show all time</button>
          </div>
        ) : null}

        {sorted.length === 0 ? (
          <ListEmpty
            message={
              q === ''
                ? `No matched deposits between ${words.from} and ${words.to}.`
                : `Nothing matches '${q}' between ${words.from} and ${words.to}.`
            }
            onSearchAllTime={allTime ? undefined : searchAllTime}
          />
        ) : (
          <>
            {groups.map((g) => (
              <Fragment key={g.iso}>
                <div className="bm-dg">{monoDay(g.iso)}</div>
                {g.rows.map((d) => (
                  <a
                    key={d.id}
                    href={`/posting/matched/${d.id}`}
                    className={`bm-row${d.id === id ? ' on' : ''}`}
                    aria-current={d.id === id ? 'true' : undefined}
                    onClick={(e) => { if (!isPlainClick(e)) return; e.preventDefault(); open(d.id); }}
                  >
                    <div className="bm-r1"><b>{d.payer}</b><span className="bm-m">{money(d.amount)}</span></div>
                    <div className="bm-r2"><span className="bm-m">{keyRef(d)}</span><span className="bm-how"><i />{HOW_LABEL[d.kind]}</span></div>
                  </a>
                ))}
              </Fragment>
            ))}
            <div className="bm-more">
              <span>Showing {shown.length.toLocaleString()} of {sorted.length.toLocaleString()}</span>
              {shown.length < sorted.length ? (
                <button type="button" className="bm-link-btn bm-strong" onClick={() => { patch({ page: String(page + 1) }); }}>
                  Load {Math.min(STEP, sorted.length - shown.length)} more
                </button>
              ) : null}
            </div>
          </>
        )}
      </aside>

      {mobileList ? null : (
        <main className="po-detail">
          {!id ? (
            <div className="po-empty"><p className="po-empty-title">{sorted.length === 0 ? 'No matched deposit to show.' : 'Pick a deposit to see how it matched.'}</p></div>
          ) : !selected ? (
            <NotFoundInPage thing="deposit" id={id} listLabel="matched deposits" listTo="/posting/matched" />
          ) : selected.unmatched ? (
            <div className="po-empty">
              <p className="po-empty-title">This deposit was unmatched.</p>
              <p className="po-meta">It is in Bank deposits, where ELVA asks what is wrong.</p>
              <div className="bm-foot" style={{ justifyContent: 'center', marginTop: 12 }}>
                <button type="button" className="po-btn po-btn-sm po-btn-primary" onClick={() => { undoUnmatch(selected.id); }}>Undo</button>
                <Link className="po-btn po-btn-sm" to="/posting/bank">Open Bank deposits</Link>
              </div>
            </div>
          ) : (
            <MatchedDetail
              key={selected.id}
              d={selected}
              {...(!isDesktop ? { onBack: () => { void navigate(`/posting/matched${search}`); } } : {})}
              onUnmatch={() => { onUnmatch(selected); }}
            />
          )}
        </main>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// the detail
// ---------------------------------------------------------------------------

function Marked({ text, hl }: { text: string; hl: string }) {
  const i = hl === '' ? -1 : text.indexOf(hl);
  if (i < 0) return <>{text}</>;
  return <>{text.slice(0, i)}<mark>{text.slice(i, i + hl.length)}</mark>{text.slice(i + hl.length)}</>;
}

interface HowRow { label: string; text: string; ev: string }

function bd(n: number): string {
  return `${String(n)} business ${n === 1 ? 'day' : 'days'}`;
}

function eraAmountEvidence(e: MatchedEra, deposit: number): { text: string; ev: string } {
  const extra = e.extras?.[0];
  if (!extra) return { text: "the deposit equals the ERA's total payment, to the cent.", ev: `${money(deposit)} = ${money(e.amount)}` };
  const sign = extra.amount < 0 ? '−' : '+';
  return {
    text: `the deposit equals the ERA's payment ${extra.amount < 0 ? 'less' : 'plus'} ${extra.label.charAt(0).toLowerCase()}${extra.label.slice(1)}, to the cent.`,
    ev: `${money(e.amount)} ${sign} ${money(extra.amount)} = ${money(deposit)}`,
  };
}

function howRows(d: MatchedDeposit): { lead: string; rows: HowRow[] } {
  const e0 = d.eras[0];
  if (d.kind === 'trace' && e0) {
    const a = eraAmountEvidence(e0, d.amount);
    return {
      lead: 'Three checks, all passed.',
      rows: [
        { label: 'Trace number', text: `the bank line carries ${e0.trace}, the same trace as ${d.payer}'s ERA.`, ev: `${e0.trace} = ${e0.trace}` },
        { label: 'Amount', ...a },
        { label: 'Timing', text: `the money arrived ${bd(businessDaysBetween(e0.dateIso, d.dateIso))} after the ERA's payment date (within 3 is normal).`, ev: `${shortDate(e0.dateIso)} → ${shortDate(d.dateIso)}` },
      ],
    };
  }
  if (d.kind === 'amount' && e0) {
    const a = eraAmountEvidence(e0, d.amount);
    const lead =
      "This bank doesn't show trace numbers, so ELVA matched by amount and date. It does that only when exactly one open payment fits; if two could fit, the deposit waits in Bank deposits for a person.";
    const timing: HowRow = { label: 'Timing', text: `the money arrived ${bd(businessDaysBetween(e0.dateIso, d.dateIso))} after the ERA's payment date (within 3 is normal).`, ev: `${shortDate(e0.dateIso)} → ${shortDate(d.dateIso)}` };
    const payer: HowRow = { label: 'Payer', text: `the bank line names ${d.payer}, the payer of this ERA.`, ev: d.bankHighlight };
    const last: HowRow = d.pickedBy
      ? { label: 'Chosen by a person', text: `two open ${d.payer} payments fit this deposit, and ${actor(d.pickedBy)} picked this one.`, ev: '1 of 2' }
      : { label: 'Only one fit', text: `no other open ${d.payer} payment or deposit has this amount within 3 business days.`, ev: '1 of 1' };
    return { lead, rows: [{ label: 'Amount', ...a }, payer, timing, last] };
  }
  if (d.kind === 'eras') {
    const lastEra = d.eras[d.eras.length - 1] ?? e0;
    const rows: HowRow[] = d.eras.map((e) => ({
      label: `ERA of ${shortDate(e.dateIso)}`,
      text: `${d.payer} paid ${money(e.amount)} on ${plural(e.claims.length, 'claim', 'claims')}.`,
      ev: `${e.trace} · ${money(e.amount)}`,
    }));
    rows.push({ label: 'Amount', text: `the ${String(d.eras.length)} ERAs add up to the deposit, to the cent.`, ev: `${d.eras.map((e) => money(e.amount)).join(' + ')} = ${money(d.amount)}` });
    rows.push({ label: 'Payer', text: `the bank line names ${d.payer}, the payer of every ERA.`, ev: d.bankHighlight });
    if (lastEra) {
      rows.push({ label: 'Timing', text: `the money arrived ${bd(businessDaysBetween(lastEra.dateIso, d.dateIso))} after the last ERA's payment date (within 3 is normal).`, ev: `${shortDate(lastEra.dateIso)} → ${shortDate(d.dateIso)}` });
    }
    return { lead: `One deposit paid ${String(d.eras.length)} ERAs.`, rows };
  }
  // checks
  const n = d.checks.length;
  const lastReceived = d.checks.reduce((m, c) => (c.receivedIso > m ? c.receivedIso : m), '');
  const rows: HowRow[] = [
    {
      label: 'Amount',
      text: n === 1 ? 'the check equals the deposit, to the cent.' : `the ${String(n)} checks add up to the deposit, to the cent.`,
      ev: `${d.checks.map((c) => money(c.amount)).join(' + ')} = ${money(d.amount)}`,
    },
  ];
  if (d.bankItems != null) {
    rows.push({ label: 'Count', text: `the deposit has ${plural(d.bankItems, 'item', 'items')}, and ${plural(n, 'check was', 'checks were')} waiting.`, ev: `${String(d.bankItems)} = ${String(n)}` });
  }
  rows.push({ label: 'Timing', text: 'the deposit is on or after the day the last check was received.', ev: `${shortDate(d.dateIso)} ≥ ${shortDate(lastReceived)}` });
  return {
    lead: 'A practice deposits several checks at once, so the bank shows one total. ELVA adds up the checks it read from the EOBs and received but not yet matched.',
    rows,
  };
}

function claimHref(claimId: string | null, patient: string): string {
  return claimId ? `/claims/record/${claimId}` : `/claims/all?q=${encodeURIComponent(patient)}`;
}

function postedHref(q: string): string {
  return `/posting/posted?q=${encodeURIComponent(q)}&range=all-time`;
}

/** A real Log entry for the payer on the posting day, or null. */
function useLogHref(d: MatchedDeposit): string | null {
  return useMemo(() => {
    const days = new Set<string>();
    const names = new Set<string>();
    if (d.kind === 'checks') {
      days.add(d.dateIso);
      for (const c of d.checks) names.add(c.payer);
    } else {
      names.add(d.payer);
      for (const e of d.eras) days.add(e.postedIso);
    }
    const hit = getLogEntriesFlat().find((e) => {
      if (e.kind !== 'posted' || !e.dateIso || !days.has(e.dateIso)) return false;
      const text = `${e.what} ${e.meta} ${e.title ?? ''}`;
      return [...names].some((n) => text.includes(n));
    });
    return hit ? `/elva-work/log/${hit.id}` : null;
  }, [d]);
}

function MatchedDetail({ d, onBack, onUnmatch }: { d: MatchedDeposit; onBack?: () => void; onUnmatch: () => void }) {
  const accounts = getAccountsSync();
  const acct = accounts.find((a) => a.id === d.accountId);
  const logHref = useLogHref(d);
  const how = howRows(d);
  const isChecks = d.kind === 'checks';
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => { headingRef.current?.focus(); }, [d.id]);

  const claims = claimTotal(d);
  const lastEra = d.eras[d.eras.length - 1];
  const sub = [acct?.short ?? '', eraWords(d), `${plural(claims, 'claim', 'claims')}, all posted in ${PMS}`].filter(Boolean).join(' · ');
  const received = isChecks ? d.checks.map((c) => c.receivedIso).sort() : [];
  const receivedLabel = received.length === 0 ? '' : spanLabel(received[0] ?? '', received[received.length - 1] ?? '');
  const uploadedDay = d.uploadedLabel.split(',')[0] ?? '';

  return (
    <div className="po-detail-inner bm-detail">
      {onBack ? <button type="button" className="po-back" style={{ padding: 0 }} onClick={onBack}>← All matched deposits</button> : null}

      <section className="bm-card">
        <div className="bm-hd">
          <div>
            <div className="bm-k">Matched deposit · {shortDate(d.dateIso)}</div>
            <h2 className="bm-h2" tabIndex={-1} ref={headingRef}>{headline(d)}</h2>
            <div className="bm-subline">{sub}</div>
          </div>
          <div className="bm-amt">
            <div className="bm-k">Deposit</div>
            <div className="bm-av num">{money(d.amount)}</div>
            <span className="bm-ok">{pill(d)}</span>
          </div>
        </div>

        <div className="bm-pair">
          <div className="bm-side">
            <div className="bm-k">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 9.5 12 4l9 5.5" /><path d="M5 10v8M9.5 10v8M14.5 10v8M19 10v8" /><path d="M3 20h18" /></svg>
              In the bank
            </div>
            <div className="bm-big num">{money(d.amount)}</div>
            <div className="bm-faint">Credit · {longDate(d.dateIso)}</div>
            <div className="bm-desc"><Marked text={d.bankLine} hl={d.bankHighlight} /></div>
            <dl>
              <dt>Account</dt><dd>{acct?.name ?? d.accountId}</dd>
              <dt>Statement</dt><dd>{d.statementFile} · uploaded {uploadedDay} by {actor(d.uploadedBy)}</dd>
              {isChecks ? (
                <>
                  <dt>Items</dt>
                  <dd>{d.bankItems != null ? `${String(d.bankItems)} (the bank shows no check numbers)` : 'not shown (the bank shows no check numbers)'}</dd>
                </>
              ) : null}
            </dl>
          </div>
          <div className="bm-link" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M9 12h6" /><path d="M15 8l4 4-4 4" /><path d="M9 8l-4 4 4 4" /></svg>
          </div>
          {isChecks ? (
            <div className="bm-side">
              <div className="bm-k">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 3h7l4 4v14H7z" /><path d="M14 3v4h4" /><path d="M10 12h5M10 16h5" /></svg>
                The checks ELVA read
              </div>
              <div className="bm-big num">{money(d.amount)}</div>
              <div className="bm-faint">{plural(d.checks.length, 'paper EOB', 'paper EOBs')} in Document Center</div>
              <dl>
                <dt>Checks</dt><dd>{d.checks.length} (below)</dd>
                <dt>Received</dt><dd>{receivedLabel}</dd>
                <dt>Posted</dt><dd>{shortDate(d.dateIso)}</dd>
              </dl>
            </div>
          ) : (
            <div className="bm-side">
              <div className="bm-k">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 3h7l4 4v14H7z" /><path d="M14 3v4h4" /><path d="M10 12h5M10 16h5" /></svg>
                What {d.payer} sent
              </div>
              <div className="bm-big num">{money(d.amount)}</div>
              <div className="bm-faint">
                {d.eras.length === 1 && lastEra ? `ERA · paid ${longDate(lastEra.dateIso)}` : `${String(d.eras.length)} ERAs · paid ${d.eras.map((e) => shortDate(e.dateIso)).join(' and ')}`}
              </div>
              <dl>
                <dt>Method</dt><dd>EFT (ACH)</dd>
                {d.eras.map((e, i) => (
                  <Fragment key={e.trace}>
                    <dt>{d.eras.length === 1 ? 'Trace' : `Trace ${String(i + 1)}`}</dt>
                    <dd className="bm-mono">
                      {d.bankTrace === e.trace ? <mark>{e.trace}</mark> : <>{e.trace} (not on the bank line)</>}
                    </dd>
                  </Fragment>
                ))}
                <dt>Claims</dt><dd>{claims} (below)</dd>
                <dt>Payment</dt>
                <dd>
                  {d.eras.length === 1 && lastEra
                    ? <Link className="bm-a" to={postedHref(lastEra.trace)}>Open it in Posted</Link>
                    : 'Open each ERA in Posted (below)'}
                </dd>
              </dl>
            </div>
          )}
        </div>
      </section>

      <section className="bm-card bm-sec">
        <h3>How ELVA matched it</h3>
        <p className="bm-lead">{how.lead}</p>
        <ul className="bm-checks">
          {how.rows.map((r) => (
            <li key={r.label}>
              <span className="bm-tk" aria-hidden="true">✓</span>
              <span><b>{r.label}</b>: {r.text}</span>
              <span className="bm-ev">{r.ev}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="bm-card bm-sec">
        {isChecks ? (
          <>
            <h3>{d.checks.length === 1 ? 'The check in this deposit' : `The ${String(d.checks.length)} checks in this deposit`}</h3>
            <table className="bm-tb">
              <thead><tr><th>Payer</th><th>Check</th><th>Claims</th><th className="r">Amount</th></tr></thead>
              <tbody>
                {d.checks.map((c) => (
                  <tr key={c.checkNumber}>
                    <td>{c.payer}</td>
                    <td className="bm-mono">{c.checkNumber}</td>
                    <td>{plural(c.claimCount, 'claim', 'claims')} · <Link to={postedHref(c.checkNumber)}>Open in Posted</Link></td>
                    <td className="r num">{money(c.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr><td>Total</td><td /><td /><td className="r num">{money(d.checks.reduce((s, c) => s + c.amount, 0))}</td></tr></tfoot>
            </table>
            <div className="bm-post">
              <span>All {String(claims)} claims posted in {PMS} {checkPostedLabel(d)} by {actor(checkPostedBy(d))} · read back: matches</span>
              {logHref ? <Link to={logHref}>See it in ELVA&apos;s log</Link> : null}
            </div>
          </>
        ) : (
          <>
            <h3>
              {d.eras.length === 1
                ? `The ${plural(claims, 'claim', 'claims')} in this payment`
                : `The ${plural(claims, 'claim', 'claims')} in these ${String(d.eras.length)} ERAs`}
            </h3>
            <table className="bm-tb">
              <thead><tr><th>Patient</th><th>Claim</th><th className="r">Paid</th></tr></thead>
              <tbody>
                {d.eras.map((e) => (
                  <Fragment key={e.trace}>
                    {d.eras.length > 1 ? (
                      <tr className="bm-erarow">
                        <td colSpan={2}>ERA of {shortDate(e.dateIso)} · <span className="bm-mono">{e.trace}</span> · <Link to={postedHref(e.trace)}>Open it in Posted</Link></td>
                        <td className="r num">{money(e.amount)}</td>
                      </tr>
                    ) : null}
                    {e.claims.map((c) => (
                      <tr key={`${e.trace}-${c.claimId ?? c.patient}`}>
                        <td>{c.patient}</td>
                        <td>
                          <Link className="bm-mono" to={claimHref(c.claimId, c.patient)}>{c.claimId ?? 'Find in claims'}</Link>
                        </td>
                        <td className="r num">{money(c.paid)}</td>
                      </tr>
                    ))}
                    {(e.extras ?? []).map((x) => (
                      <tr key={`${e.trace}-${x.label}`}>
                        <td colSpan={2}>{x.label}</td>
                        <td className="r num">{x.amount < 0 ? `−${money(x.amount)}` : money(x.amount)}</td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
              <tfoot><tr><td>Total</td><td /><td className="r num">{money(d.amount)}</td></tr></tfoot>
            </table>
            {lastEra ? (
              <div className="bm-post">
                <span>Posted in {PMS} {lastEra.postedLabel} by {actor(lastEra.postedBy)} · read back: matches</span>
                {logHref ? <Link to={logHref}>See it in ELVA&apos;s log</Link> : null}
              </div>
            ) : null}
          </>
        )}
      </section>

      <section className="bm-card bm-foot">
        <button type="button" className="bm-ghost" onClick={onUnmatch}>This isn&apos;t right</button>
        <span className="bm-note">Unmatches the deposit and moves it to Bank deposits, where ELVA asks what&apos;s wrong.</span>
      </section>
    </div>
  );
}
